import { Toast } from '@easyvaas/js-components';
import classNames from 'classnames';
import React, { forwardRef } from 'react';
import { areEqual, FixedSizeList, ListChildComponentProps } from 'react-window';
import { useRecoilValue } from 'recoil';
import { levels } from 'src/modules/imagesV3/constants/levels';
import { types } from 'src/modules/imagesV3/constants/types';
import { useOpenPopup } from 'src/modules/imagesV3/hooks/useOpenPopup';
import {
  cancelDefaultImage,
  setDefaultImage,
  imagesV3Data
} from 'src/modules/imagesV3/services';
import {
  imageItemSelector,
  myImagesListSelector
} from 'src/modules/imagesV3/store/selector';
import EmptyPlaceholder from '../../EmptyPlaceholder';

import ImageButton from '../../ImageButton';
import ImageStars from '../../ImageStars';
import BaseListItem from '../BaseListItem';
import { LIST_HEIGHT, LIST_WIDTH, RATIO } from '../list';
import styles from './index.module.scss';

const calcStyle = (style: any) => {
  return {
    ...style,
    top: `${parseFloat(style.top) + 10 * RATIO}px`
  };
};

const Row = React.memo(
  ({ index, style, data }: ListChildComponentProps<number[]>) => {
    const item = useRecoilValue(imageItemSelector(data[index]));
    const openPopup = useOpenPopup();
    const onDefaultClick = async () => {
      try {
        if (!item) return null;
        const fn = item.isDefault ? cancelDefaultImage : setDefaultImage;
        Toast.loading('请稍等...');
        await fn({ imgId: item.id });
        await imagesV3Data.refresh({});
        Toast(`${item.isDefault ? '取消' : '设置'}成功`);
      } catch ({ message }) {
        Toast(message);
      }
    };
    if (!item) return null;
    return (
      <div className={styles.wrapper} style={calcStyle(style)}>
        <BaseListItem
          className={classNames(styles.item, item.level === 4 && styles.level4)}
          logo={item.url}
          title={item.title}
          desc={item.desc}
          itemsInsideLogo={[
            item.upStar && item.upStar.level > 0 && (
              <div className={styles.stars}>
                <ImageStars count={item.upStar.level} />
              </div>
            ),
            (item.level === 4 || item.type === 4) && (
              <button
                className={styles.ability}
                onClick={() => openPopup('abilities', item.id)}
              >
                <span>查看天赋</span>
              </button>
            )
          ]}
          buttons={[
            <ImageButton type="primary" onClick={onDefaultClick}>
              {item.isDefault ? '取消默认' : '设为默认'}
            </ImageButton>,
            item.advance && (
              <ImageButton
                type={item.level === 3 ? 'accent' : 'primary'}
                onClick={() => openPopup('evolution', item.id, 1)}
              >
                {item.level === 3 && '高级'}进阶
              </ImageButton>
            ),
            item.piecesResolved > 0 && (
              <ImageButton
                type="primary"
                onClick={() => openPopup('exchange', item.id, 1)}
              >
                分解
              </ImageButton>
            )
          ]}
          labels={[
            <div className={styles.type}>
              <span>{types[item.type]}</span>
            </div>,
            item.level > 0 && (
              <div
                className={classNames(
                  styles.level,
                  item.level === 4 && styles.level4
                )}
              >
                <span>{levels(item.level)}</span>
              </div>
            )
          ]}
        />
      </div>
    );
  },
  areEqual
);

const InnerElementType: React.FC = forwardRef<any, any>(
  ({ style, ...rest }, ref) => (
    <div
      ref={ref}
      style={{
        ...style,
        height: `${parseFloat(style.height) + 30 * RATIO}px`
      }}
      {...rest}
    />
  )
);

const MyImagesList: React.FC = () => {
  const list = useRecoilValue(myImagesListSelector);
  if (list.length === 0) return <EmptyPlaceholder />;
  return (
    <FixedSizeList
      height={LIST_HEIGHT}
      itemCount={list.length}
      itemData={list}
      itemSize={280 * RATIO}
      width={LIST_WIDTH}
      className={styles.container}
      innerElementType={InnerElementType}
    >
      {Row}
    </FixedSizeList>
  );
};

export default React.memo(MyImagesList);
